<script setup>
import { computed, shallowRef } from 'vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiRowActions from '@/components/ui/UiRowActions.vue';
import PageDialog from './PageDialog.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useConfirm } from '@/composables/useConfirm';
import { useDialogs } from '@/composables/useDialogs';
import { useListResource } from '@/composables/useListResource';
import { useToast } from '@/composables/useToast';

// The catalog of pages and menu groups that roles pick from.
const SYSTEM_ROUTES = ['/user-modules', '/school-settings', '/activity-log'];

const search = shallowRef('');
const roles = shallowRef([]);
const { items, loading, error, load } = useListResource(async () => {
  const [pages, roleRows] = await Promise.all([
    apiRequest('get', '/sys-modules'),
    apiRequest('get', '/assigned-modules'),
  ]);
  roles.value = Array.isArray(roleRows) ? roleRows : [];
  return pages;
});
load();

// Which roles' menus include each page (selected_modules holds page ids).
const usedBy = computed(() => {
  const map = new Map();
  for (const role of roles.value) {
    let ids = [];
    try {
      ids = JSON.parse(role.selected_modules || '[]').map(Number);
    } catch {
      ids = [];
    }
    for (const id of ids) {
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(role.description);
    }
  }
  return map;
});

const rows = computed(() =>
  items.value.map((p) => ({
    ...p,
    isGroup: Number(p.isParent) === 1,
    roles: usedBy.value.get(Number(p.id)) ?? [],
    system: SYSTEM_ROUTES.includes(p.route),
  })),
);
const groups = computed(() => items.value.filter((p) => Number(p.isParent) === 1));

const headers = [
  { title: 'Page', key: 'title' },
  { title: 'Address', key: 'route' },
  { title: 'Type', key: 'isGroup', width: 100 },
  { title: 'Used in', key: 'roles', sortable: false },
  { title: '', key: 'actions', align: 'end', sortable: false, width: 72 },
];

const dialogs = useDialogs();
const formOpen = dialogs.model('form');
const { confirm } = useConfirm();
const toast = useToast();

async function remove(page) {
  const ok = await confirm({
    title: 'Delete page?',
    description: `${page.title} will be removed from the catalog. Pages still in a role's menu can't be deleted.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (!ok) return;
  try {
    assertOk(await apiRequest('delete', `/sys-modules/${page.id}`));
    toast.success('Page deleted.');
    load();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not delete the page.'));
  }
}
</script>

<template>
  <UiCard
    title="List of modules"
    description="Every page and menu group a role can add to its sidebar."
    flush
  >
    <template #toolbar>
      <div class="ui-card__toolbar-end">
        <v-text-field
          v-model="search"
          placeholder="Search pages"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
          class="card-search"
        />
        <v-btn color="primary" prepend-icon="mdi-plus" @click="dialogs.open('form')">
          Add page
        </v-btn>
      </div>
    </template>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-6 mb-4">
      {{ error }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="rows"
      :search="search"
      :loading="loading"
      :items-per-page="25"
      class="table-flush"
    >
      <template #[`item.title`]="{ item }">
        <div class="d-flex align-center ga-3">
          <v-icon size="18" class="text-medium-emphasis">{{ item.icon || 'mdi-file-outline' }}</v-icon>
          <span class="font-weight-medium">{{ item.title }}</span>
          <UiBadge v-if="item.system" variant="outline">System</UiBadge>
        </div>
      </template>
      <template #[`item.route`]="{ item }">
        <code v-if="item.route" class="route">{{ item.route }}</code>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #[`item.isGroup`]="{ item }">
        <UiBadge :variant="item.isGroup ? 'violet' : 'secondary'">
          {{ item.isGroup ? 'Group' : 'Page' }}
        </UiBadge>
      </template>
      <template #[`item.roles`]="{ item }">
        <div v-if="item.roles.length" class="d-flex flex-wrap ga-1">
          <UiBadge v-for="r in item.roles" :key="r" variant="outline">{{ r }}</UiBadge>
        </div>
        <span v-else class="text-medium-emphasis">Not used</span>
      </template>
      <template #[`item.actions`]="{ item }">
        <UiRowActions
          :label="`Actions for ${item.title}`"
          :items="[
            { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => dialogs.open('form', item) },
            { title: 'Delete', icon: 'mdi-trash-can-outline', destructive: true, hidden: item.system, onClick: () => remove(item) },
          ]"
        />
      </template>
      <template #no-data>
        <div class="table-empty">No pages yet.</div>
      </template>
    </v-data-table>
  </UiCard>

  <PageDialog v-model="formOpen" :page="dialogs.selected.value" :groups="groups" @saved="load" />
</template>

<style scoped>
.route {
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--accent);
  color: var(--secondary-foreground);
  font-size: 12px;
}
</style>
